import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import appIcon from '../../assets/landing/app-icon.png';
import { useLoginMutation } from '../../redux/auth/auth.api';
import { useAppSelector } from '../../redux/store';
import '../../styles/landing.css';

const ADMIN_ROLE = 3;

interface FetchBaseQueryErrorShape {
  status?: number | string;
  data?: { message?: string };
}

const extractErrorMessage = (err: unknown): string => {
  if (typeof err === 'object' && err !== null) {
    const e = err as FetchBaseQueryErrorShape;
    if (e.data?.message) return e.data.message;
    if (e.status === 'FETCH_ERROR') return 'Network error. Check your connection.';
  }
  return 'Sign in failed';
};

export const AdminLogin = (): React.JSX.Element => {
  const navigate = useNavigate();
  const admin = useAppSelector((state) => state.auth.admin);
  const [login, { isLoading }] = useLoginMutation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (admin && admin.role === ADMIN_ROLE) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [admin, navigate]);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    try {
      const data = await login({
        email: email.trim().toLowerCase(),
        password,
      }).unwrap();
      if (data.user.role !== ADMIN_ROLE) {
        setError('This account does not have admin access.');
        return;
      }
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <div className="admin-shell">
      <div className="admin-card">
        <div className="admin-brand">
          <img src={appIcon} alt="" className="admin-brand__icon" />
          <span className="admin-brand__name">
            gather <span className="admin-brand__tag">admin</span>
          </span>
        </div>
        <h1 className="admin-title">Sign in</h1>
        <p className="admin-sub">Admins only. Other accounts will be rejected.</p>
        <form className="admin-form" onSubmit={onSubmit} noValidate>
          <label className="admin-field">
            <span className="admin-field__label">Email</span>
            <input
              className="admin-field__input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </label>
          <label className="admin-field">
            <span className="admin-field__label">Password</span>
            <input
              className="admin-field__input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </label>
          {error && <div className="admin-error">{error}</div>}
          <button
            type="submit"
            className="btn btn-primary admin-submit"
            disabled={isLoading || !email || !password}
          >
            {isLoading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};
